import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const linkId = 'fontshare-cabinet-grotesk';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/portfolio", label: "Portfolio" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" }
  ];

  return (
    <nav className={`fixed w-full z-[100] transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-2 md:py-3' : 'bg-transparent py-5 md:py-8'}`}>
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 md:px-12 flex justify-between items-center relative">
        
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer group">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/29d79c4109d1c19c2e75790b8e50872c9a33c9d5ce79f6f43f60effe48b91ada6d4fe9244bdd866a6b5eb503d1d2a9a7/images/aW1hZ2UvcG5n/ceb9c963bc5f7292bff620425526a4d8.png" 
            alt="HostFig Logo" 
            className={`object-contain transition-all duration-300 ${isScrolled ? 'h-10 md:h-12 lg:h-14' : 'h-16 md:h-20 lg:h-24'}`}
          />
        </Link>

        {/* Desktop Links (Pill) */}
        <div 
          id="menu-box"
          className="hidden lg:flex shadow-sm select-none" 
          style={{ 
            padding: '12px 25px', 
            alignItems: 'center', 
            gap: '30px', 
            borderRadius: '40px', 
            background: '#F3F3F3' 
          }}
        >
          {navLinks.map(link => (
            <Link 
              key={link.path} 
              to={link.path} 
              className="text-[#000] text-[15px] font-medium hover:opacity-60 transition-opacity duration-200"
              style={{ fontFamily: '"Cabinet Grotesk", sans-serif' }}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <motion.button 
          id="btn-explore-nav"
          layout
          onClick={() => navigate('/contact')}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ 
            flexDirection: isHovered ? 'row-reverse' : 'row',
            padding: isHovered ? '8px 8px 8px 20px' : '8px 20px 8px 8px',
            borderRadius: '40px',
            background: '#F3F3F3',
            fontFamily: '"Cabinet Grotesk", sans-serif'
          }}
          className="hidden lg:flex items-center gap-2 hover:bg-[#e8e8e8] shadow-sm transition-colors duration-300 ease-in-out cursor-pointer"
        >
          <motion.div layout className="w-[26px] h-[26px] flex-shrink-0">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <rect width="26" height="26" rx="13" fill="black"/>
              <path d="M7.26465 13.0003H17.9705M13.3823 17.5886L17.9705 13.0003L13.3823 8.41211" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
          <motion.span layout className="text-[16px] font-normal text-black whitespace-nowrap">
            Get Started
          </motion.span>
        </motion.button>

        {/* Mobile Menu Button */}
        <button 
          id="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex lg:hidden relative z-50 w-[44px] h-[44px] items-center justify-center active:scale-90 transition-transform"
        >
          {isMobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3" strokeLinecap="round">
              <path d="M4.5 4.5L19.5 19.5"/>
              <path d="M19.5 4.5L4.5 19.5"/>
            </svg>
          ) : (
            <svg width="30" height="19" viewBox="0 0 30 19" fill="none">
              <rect y="0" width="30" height="3" rx="1.5" fill="black"/>
              <rect y="8" width="30" height="3" rx="1.5" fill="black"/>
              <rect y="16" width="30" height="3" rx="1.5" fill="black"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div 
              id="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/10 backdrop-blur-sm z-40 lg:hidden"
              style={{ top: isScrolled ? '72px' : '96px' }}
            />
            <motion.div 
              id="mobile-menu-panel"
              initial={{ y: -15, scale: 0.95, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: -15, scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              style={{ 
                boxShadow: "0 20px 40px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.02)",
                fontFamily: '"Cabinet Grotesk", sans-serif',
                top: isScrolled ? '72px' : '96px'
              }}
              className="absolute left-4 right-4 bg-white/95 backdrop-blur-md border border-[#EBEBEB] rounded-3xl p-5 z-50 flex flex-col gap-4 lg:hidden"
            >
              <div className="flex flex-col">
                {navLinks.map(link => (
                  <Link 
                    key={link.path} 
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="py-2.5 px-4 hover:bg-[#F3F3F3] active:bg-[#ebebeb] rounded-2xl text-[17px] font-medium text-[#000]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="w-full h-[1px] bg-neutral-100 my-1" />
              <button 
                onClick={() => { setIsMobileMenuOpen(false); navigate('/contact'); }}
                className="w-full bg-[#000] text-[#fff] rounded-[20px] p-[12px] hover:bg-[#1a1a1a] active:scale-[0.98] text-[15px] font-medium transition-all"
              >
                Get Started
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;